import { AxiosError } from 'axios';
import { useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router';
import {
  type AiProposal,
  createLocation,
  deleteLocationMedia,
  generateLocationDescriptionTask,
  getLocation,
  getAiTask,
  getApiErrorMessage,
  importLocationIntoExistingFromJson,
  importLocationFromJson,
  listAiProposals,
  listCategories,
  listCounties,
  listLocationBestSeasons,
  listLocationDifficulties,
  listLocationRoadTypes,
  listAllLocationMedia,
  listCountries,
  listRegions,
  listSettlements,
  listTags,
  patchLocation,
  publishLocation,
  reorderLocationMedia,
  resubmitLocation,
  setLocationCover,
  suggestLocation,
  uploadLocationMedia,
  type CreateLocationPayload,
  type DictionaryItem,
  type LocationDictionaryItem,
  type LocationMedia,
  type LocationStatus,
} from '../lib/api';
import { CONTENT_BLOCK_MEDIA_TITLE_PREFIX, isContentBlockMediaTitle, stringifyContentBlocks } from '../lib/contentBlocks';
import { createRichContentEditor } from '../lib/richContentEditor';
import { CREATE_LOCATION_TEMPLATE } from '../templates/createLocationTemplate';

type PendingMedia = { file: File; url: string };
type FactItem = { id: number; text: string };
type DragMediaPayload = { kind: 'pending' | 'existing'; index: number };

const CYRILLIC_MAP: Record<string, string> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ё: 'yo',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'y',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'h',
  ц: 'ts',
  ч: 'ch',
  ш: 'sh',
  щ: 'sch',
  ъ: '',
  ы: 'y',
  ь: '',
  э: 'e',
  ю: 'yu',
  я: 'ya',
};

const transliterate = (value: string): string =>
  value
    .split('')
    .map((char) => {
      const lower = char.toLowerCase();
      return CYRILLIC_MAP[lower] ?? char;
    })
    .join('');

const slugify = (value: string): string =>
  transliterate(value)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const isFallbackSlug = (value: string): boolean => {
  const slug = value.trim().toLowerCase();
  if (!slug) return false;
  if (/^location-[a-f0-9]{12}(?:-\d+)?$/i.test(slug)) return true;
  if (slug === 'item' || slug.startsWith('item-')) return true;
  return false;
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

export default function CreateLocationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const template = useMemo(() => CREATE_LOCATION_TEMPLATE, []);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const titleInput = root.querySelector<HTMLInputElement>('#create-location-title');
    const slugInput = root.querySelector<HTMLInputElement>('#create-location-slug');
    const slugPrefix = root.querySelector<HTMLElement>('#create-location-slug-prefix');
    const descriptionInput = root.querySelector<HTMLTextAreaElement>('#create-location-description');
    const contentBlocksInput = root.querySelector<HTMLTextAreaElement>('#create-location-content-blocks');
    const contentBlocksEditorRoot = root.querySelector<HTMLElement>('#create-location-content-blocks-editor');
    const roadTypeSelect = root.querySelector<HTMLSelectElement>('#create-location-road-type');
    const difficultySelect = root.querySelector<HTMLSelectElement>('#create-location-difficulty');
    const bestSeasonSelect = root.querySelector<HTMLSelectElement>('#create-location-best-season');
    const latInput = root.querySelector<HTMLInputElement>('#create-location-latitude');
    const lonInput = root.querySelector<HTMLInputElement>('#create-location-longitude');
    const countrySelect = root.querySelector<HTMLSelectElement>('#create-location-country');
    const regionSelect = root.querySelector<HTMLSelectElement>('#create-location-region');
    const countySelect = root.querySelector<HTMLSelectElement>('#create-location-county');
    const settlementSelect = root.querySelector<HTMLSelectElement>('#create-location-settlement');
    const safetyNotesInput = root.querySelector<HTMLTextAreaElement>('#create-location-safety-notes');
    const accessRulesInput = root.querySelector<HTMLTextAreaElement>('#create-location-access-rules');
    const featuredToggle = root.querySelector<HTMLButtonElement>('#create-location-featured-toggle');
    const publishBtn = root.querySelector<HTMLButtonElement>('#create-location-publish-btn');
    const draftBtn = root.querySelector<HTMLButtonElement>('#create-location-save-draft-btn');
    const suggestBtn = root.querySelector<HTMLButtonElement>('#create-location-suggest-btn');
    const cancelBtn = root.querySelector<HTMLButtonElement>('#create-location-cancel-btn');
    const importBtn = root.querySelector<HTMLButtonElement>('#create-location-import-btn');
    const aiBtn = root.querySelector<HTMLButtonElement>('#create-location-ai-btn');
    const mediaBtn = root.querySelector<HTMLButtonElement>('#create-location-media-btn');
    const mediaInput = root.querySelector<HTMLInputElement>('#create-location-media-input');
    const mediaPreview = root.querySelector<HTMLElement>('#create-location-media-preview');
    const coverBtn = root.querySelector<HTMLButtonElement>('#create-location-cover-btn');
    const coverInput = root.querySelector<HTMLInputElement>('#create-location-cover-input');
    const coverName = root.querySelector<HTMLElement>('#create-location-cover-name');
    const coverPreview = root.querySelector<HTMLElement>('#create-location-cover-preview');
    const statusBadge = root.querySelector<HTMLElement>('#create-location-status-badge');
    const lastModifiedLabel = root.querySelector<HTMLElement>('#create-location-last-modified');
    const factsList = root.querySelector<HTMLElement>('#create-location-facts-list');
    const addFactBtn = root.querySelector<HTMLButtonElement>('#create-location-add-fact-btn');
    const categorySelect = root.querySelector<HTMLSelectElement>('#create-location-category-select');
    const categoryAddBtn = root.querySelector<HTMLButtonElement>('#create-location-category-add-btn');
    const categoriesSelected = root.querySelector<HTMLElement>('#create-location-categories-selected');
    const tagSelect = root.querySelector<HTMLSelectElement>('#create-location-tag-select');
    const tagAddBtn = root.querySelector<HTMLButtonElement>('#create-location-tag-add-btn');
    const tagsSelected = root.querySelector<HTMLElement>('#create-location-tags-selected');
    const mapContainer = root.querySelector<HTMLElement>('#create-location-map');
    const mapPlaceholder = root.querySelector<HTMLElement>('#create-location-map-placeholder');
    const header = root.querySelector<HTMLElement>('header');

    if (
      !titleInput ||
      !slugInput ||
      !slugPrefix ||
      !descriptionInput ||
      !contentBlocksInput ||
      !contentBlocksEditorRoot ||
      !roadTypeSelect ||
      !difficultySelect ||
      !bestSeasonSelect ||
      !latInput ||
      !lonInput ||
      !countrySelect ||
      !regionSelect ||
      !countySelect ||
      !settlementSelect ||
      !safetyNotesInput ||
      !accessRulesInput ||
      !featuredToggle ||
      !publishBtn ||
      !draftBtn ||
      !suggestBtn ||
      !cancelBtn ||
      !importBtn ||
      !aiBtn ||
      !mediaBtn ||
      !mediaInput ||
      !mediaPreview ||
      !coverBtn ||
      !coverInput ||
      !coverName ||
      !coverPreview ||
      !statusBadge ||
      !lastModifiedLabel ||
      !factsList ||
      !addFactBtn ||
      !categorySelect ||
      !categoryAddBtn ||
      !categoriesSelected ||
      !tagSelect ||
      !tagAddBtn ||
      !tagsSelected ||
      !mapContainer ||
      !mapPlaceholder ||
      !header
    ) {
      return;
    }

    let disposed = false;
    let featured = true;
    let editingId: number | null = null;
    let editingStatus: LocationStatus | null = null;
    let slugManuallyEdited = false;
    let countries: DictionaryItem[] = [];
    let regions: Array<DictionaryItem & { country: number }> = [];
    let counties: Array<DictionaryItem & { region: number }> = [];
    let settlements: Array<DictionaryItem & { county: number }> = [];
    let categories: DictionaryItem[] = [];
    let tags: DictionaryItem[] = [];
    let roadTypes: LocationDictionaryItem[] = [];
    let difficulties: LocationDictionaryItem[] = [];
    let bestSeasons: LocationDictionaryItem[] = [];
    let pendingMedia: PendingMedia[] = [];
    let existingMedia: LocationMedia[] = [];
    let selectedCover: File | null = null;
    let selectedCoverMediaId: number | null = null;
    let selectedCoverRemoteUrl: string | null = null;
    let coverObjectUrl: string | null = null;
    let selectedCategoryIds: number[] = [];
    let selectedTagIds: number[] = [];
    let facts: FactItem[] = [];
    let factCounter = 1;
    let presetCountryId: number | null = null;
    let presetRegionId: number | null = null;
    let presetCountyId: number | null = null;
    let presetSettlementId: number | null = null;
    let presetRoadTypeId: number | null = null;
    let presetDifficultyId: number | null = null;
    let presetBestSeasonId: number | null = null;
    let mapInstance: {
      remove: () => void;
      panTo: (coords: [number, number]) => void;
      getZoom: () => number;
      setView: (coords: [number, number], zoom?: number, options?: { animate?: boolean }) => void;
      invalidateSize: () => void;
    } | null = null;
    let markerInstance: { setLatLng: (coords: [number, number]) => void } | null = null;
    let modifiedAt: Date = new Date();
    let modifiedTimer: number | null = null;
    const contentBlocksEditor = createRichContentEditor({
      root: contentBlocksEditorRoot,
      onChange: (blocks) => {
        contentBlocksInput.value = stringifyContentBlocks(blocks);
      },
      getTagOptions: () => tags.map((item) => ({ id: item.id, name: item.name, slug: item.slug })),
      uploadImage: async (file) => {
        if (!editingId) {
          throw new Error('Сначала сохраните локацию как черновик, затем загружайте изображения в блоки.');
        }
        const formData = new FormData();
        formData.append('kind', 'image');
        formData.append('file', file);
        formData.append('title', `${CONTENT_BLOCK_MEDIA_TITLE_PREFIX}${file.name}`);
        const media = await uploadLocationMedia(editingId, formData);
        return media.file_large_url || media.file_url;
      },
    });

    const flash = document.createElement('div');
    flash.className = 'hidden mt-3 text-sm font-medium';
    header.insertAdjacentElement('afterend', flash);

    const imagePreviewModal = document.createElement('div');
    imagePreviewModal.className =
      'fixed inset-0 z-[1400] hidden items-center justify-center bg-slate-950/80 p-4 cursor-zoom-out';
    imagePreviewModal.innerHTML = `
      <button data-preview-close="true" class="absolute right-4 top-4 size-9 rounded-full bg-white/90 text-slate-800 hover:bg-white" type="button">
        <span class="material-symbols-outlined text-base">close</span>
      </button>
      <img data-preview-image="true" class="max-h-[88vh] max-w-[94vw] rounded-xl object-contain shadow-2xl" alt="Image preview" />
    `;
    document.body.append(imagePreviewModal);
    const previewImage = imagePreviewModal.querySelector<HTMLImageElement>('[data-preview-image="true"]');

    const extractValidationMessage = (error: unknown, fallback: string): string => {
      const axiosError = error as AxiosError<{
        error?: { message?: string; details?: Record<string, unknown> | unknown[] };
      }>;
      const details = axiosError.response?.data?.error?.details;
      if (details && typeof details === 'object' && !Array.isArray(details)) {
        const firstKey = Object.keys(details)[0];
        const firstValue = (details as Record<string, unknown>)[firstKey];
        if (Array.isArray(firstValue) && firstValue.length > 0) {
          return `${firstKey}: ${String(firstValue[0])}`;
        }
        if (typeof firstValue === 'string') {
          return `${firstKey}: ${firstValue}`;
        }
      }
      return getApiErrorMessage(error, fallback);
    };

    const returnParam = searchParams.get('return');
    const buildReturnUrl = (): string => {
      let base = '/locations';
      if (returnParam) {
        try {
          base = decodeURIComponent(returnParam);
        } catch {
          base = returnParam;
        }
      }
      return base;
    };

    const navigateBackToList = () => {
      navigate(buildReturnUrl());
    };

    const normalizeCoordinate = (
      raw: string,
      min: number,
      max: number
    ): { value: string; numberValue: number } | null => {
      const parsed = Number(raw.trim().replace(',', '.'));
      if (Number.isNaN(parsed)) return null;
      if (parsed < min || parsed > max) return null;
      const fixed = parsed.toFixed(6);
      return { value: fixed, numberValue: Number(fixed) };
    };

    const showMessage = (message: string, type: 'error' | 'success') => {
      flash.className =
        type === 'error'
          ? 'mx-8 mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700'
          : 'mx-8 mt-3 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm font-medium text-green-700';
      flash.textContent = message;
    };

    const setStatusBadge = (status: LocationStatus | 'draft') => {
      if (status === 'published') {
        statusBadge.className = 'px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-bold';
        statusBadge.textContent = 'Published';
        return;
      }
      if (status === 'pending_review') {
        statusBadge.className = 'px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-bold';
        statusBadge.textContent = 'Pending';
        return;
      }
      if (status === 'approved') {
        statusBadge.className = 'px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-bold';
        statusBadge.textContent = 'Approved';
        return;
      }
      if (status === 'rejected') {
        statusBadge.className = 'px-2 py-0.5 bg-red-100 text-red-700 rounded-full font-bold';
        statusBadge.textContent = 'Rejected';
        return;
      }
      statusBadge.className = 'px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full font-bold';
      statusBadge.textContent = 'Draft';
    };

    const updateSlugPrefix = () => {
      slugPrefix.textContent = 'https://motojourney.by/locations/';
    };

    const humanizeRelativeTime = (value: Date): string => {
      const seconds = Math.max(0, Math.floor((Date.now() - value.getTime()) / 1000));
      if (seconds < 10) return 'just now';
      if (seconds < 60) return `${seconds}s ago`;
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      if (days < 7) return `${days}d ago`;
      return value.toLocaleString();
    };

    const renderLastModified = () => {
      lastModifiedLabel.textContent = humanizeRelativeTime(modifiedAt);
      lastModifiedLabel.title = modifiedAt.toLocaleString();
    };

    const setButtonLoading = (loading: boolean, mode: 'publish' | 'draft' | 'suggest') => {
      publishBtn.disabled = loading;
      draftBtn.disabled = loading;
      suggestBtn.disabled = loading;
      cancelBtn.disabled = loading;
      importBtn.disabled = loading;
      aiBtn.disabled = loading;
      publishBtn.textContent = loading && mode === 'publish' ? 'Publishing...' : 'Publish Location';
      draftBtn.textContent = loading && mode === 'draft' ? 'Saving...' : 'Save Draft';
      suggestBtn.textContent = loading && mode === 'suggest' ? 'Submitting...' : 'Submit Suggestion';
    };

    const setAiLoading = (loading: boolean) => {
      aiBtn.disabled = loading;
      importBtn.disabled = loading;
      aiBtn.innerHTML = loading
        ? '<span class="material-symbols-outlined text-sm animate-spin">progress_activity</span> Generating...'
        : '<span class="material-symbols-outlined text-sm">auto_awesome</span> Generate with AI';
    };

    const setImportLoading = (loading: boolean) => {
      importBtn.disabled = loading;
      aiBtn.disabled = loading;
      importBtn.innerHTML = loading
        ? '<span class="material-symbols-outlined text-sm animate-spin">progress_activity</span> Importing...'
        : '<span class="material-symbols-outlined text-sm">upload_file</span> Import JSON';
    };

    const openJsonImportDialog = (): Promise<string | null> =>
      new Promise((resolve) => {
        const overlay = document.createElement('div');
        overlay.className = 'fixed inset-0 z-[1500] flex items-center justify-center bg-slate-950/70 p-4';
        overlay.innerHTML = `
          <div class="w-full max-w-3xl rounded-xl border border-slate-200 bg-white p-5 shadow-2xl">
            <div class="mb-3 flex items-center justify-between">
              <h3 class="text-base font-bold text-slate-900">Import Location JSON</h3>
              <button data-import-close="true" class="size-8 rounded-full border border-slate-300 text-slate-500 hover:bg-slate-100" type="button">
                <span class="material-symbols-outlined text-sm">close</span>
              </button>
            </div>
            <p class="mb-3 text-xs text-slate-500">Paste full JSON payload for location import.</p>
            <textarea data-import-json="true" class="h-72 w-full rounded-lg border border-slate-300 bg-slate-50 p-3 font-mono text-xs focus:border-primary focus:ring-primary" placeholder='{"title":"...","latitude":...,"longitude":...}'></textarea>
            <div class="mt-4 flex justify-end gap-2">
              <button data-import-cancel="true" class="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100" type="button">Cancel</button>
              <button data-import-confirm="true" class="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-blue-600" type="button">Import</button>
            </div>
          </div>
        `;
        document.body.append(overlay);
        document.body.classList.add('overflow-hidden');

        const textarea = overlay.querySelector<HTMLTextAreaElement>('textarea[data-import-json="true"]');
        const close = () => {
          overlay.remove();
          document.body.classList.remove('overflow-hidden');
        };

        const onClick = (event: Event) => {
          const target = event.target as HTMLElement;
          if (target === overlay || target.closest('[data-import-close="true"]') || target.closest('[data-import-cancel="true"]')) {
            close();
            resolve(null);
            return;
          }
          if (target.closest('[data-import-confirm="true"]')) {
            const value = textarea?.value ?? '';
            close();
            resolve(value);
          }
        };

        const onKeydown = (event: KeyboardEvent) => {
          if (event.key !== 'Escape') return;
          document.removeEventListener('keydown', onKeydown);
          close();
          resolve(null);
        };

        overlay.addEventListener('click', onClick, { once: false });
        document.addEventListener('keydown', onKeydown, { once: true });
        window.setTimeout(() => textarea?.focus(), 0);
      });

    const applyFeaturedUi = () => {
      featuredToggle.className = featured
        ? 'relative inline-flex h-6 w-11 items-center rounded-full bg-primary'
        : 'relative inline-flex h-6 w-11 items-center rounded-full bg-slate-300';
      const knob = featuredToggle.querySelector('span.inline-block');
      if (knob) {
        knob.className = featured
          ? 'inline-block h-4 w-4 translate-x-6 transform rounded-full bg-white transition'
          : 'inline-block h-4 w-4 translate-x-1 transform rounded-full bg-white transition';
      }
    };

    const renderFacts = () => {
      factsList.innerHTML = facts
        .map(
          (fact) => `
            <div class="flex items-center gap-2">
              <input data-fact-id="${fact.id}" class="flex-1 bg-slate-50 border-slate-200 rounded-lg px-3 py-2 text-sm" value="${escapeHtml(fact.text)}" placeholder="Fact text"/>
              <button data-remove-fact-id="${fact.id}" class="size-8 rounded-md border border-red-200 text-red-600" type="button">
                <span class="material-symbols-outlined text-sm">delete</span>
              </button>
            </div>
          `
        )
        .join('');
    };

    const renderSelectedChips = (
      container: HTMLElement,
      selectedIds: number[],
      source: DictionaryItem[],
      type: 'category' | 'tag'
    ) => {
      container.innerHTML = selectedIds
        .map((id) => {
          const item = source.find((entry) => entry.id === id);
          if (!item) return '';
          return `
            <span class="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-xs font-bold rounded-md">
              ${escapeHtml(item.name)}
              <button data-remove-${type}-id="${id}" class="material-symbols-outlined text-[14px] cursor-pointer" type="button">close</button>
            </span>
          `;
        })
        .join('');
    };

    const renderMediaPreview = () => {
      const existingHtml = existingMedia
        .slice(0, 12)
        .map(
          (item, index) => `
            <div class="relative aspect-video rounded-lg overflow-hidden group border border-slate-200 cursor-pointer" draggable="true" data-drag-media="existing" data-drag-index="${index}" data-preview-url="${item.file_large_url || item.file_url}" data-preview-alt="media-${item.id}">
              <img class="w-full h-full object-cover pointer-events-none" src="${item.file_thumbnail_url || item.file_large_url || item.file_url}" alt="media-${item.id}" />
              <div class="absolute top-1 left-1 flex gap-1">
                <button data-existing-media-move="up" data-existing-media-index="${index}" class="size-6 bg-white text-slate-700 rounded-full flex items-center justify-center opacity-90" type="button">
                  <span class="material-symbols-outlined text-xs">arrow_upward</span>
                </button>
                <button data-existing-media-move="down" data-existing-media-index="${index}" class="size-6 bg-white text-slate-700 rounded-full flex items-center justify-center opacity-90" type="button">
                  <span class="material-symbols-outlined text-xs">arrow_downward</span>
                </button>
              </div>
              <button data-existing-media-id="${item.id}" class="absolute top-1 right-1 size-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-90" type="button">
                <span class="material-symbols-outlined text-xs">close</span>
              </button>
            </div>
          `
        )
        .join('');

      const pendingHtml = pendingMedia
        .map(
          (media, index) => `
            <div class="relative aspect-video rounded-lg overflow-hidden group border border-slate-200 bg-slate-50 cursor-pointer" draggable="true" data-drag-media="pending" data-drag-index="${index}" data-preview-url="${media.url}" data-preview-alt="${escapeHtml(media.file.name)}">
              <img class="w-full h-full object-cover pointer-events-none" src="${media.url}" alt="${escapeHtml(media.file.name)}" />
              <button data-pending-media-index="${index}" class="absolute top-1 right-1 size-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-90" type="button">
                <span class="material-symbols-outlined text-xs">close</span>
              </button>
            </div>
          `
        )
        .join('');

      mediaPreview.innerHTML = existingHtml + pendingHtml || '<div class="text-xs text-slate-500">No media selected.</div>';
    };

    const renderCoverPreview = (coverUrl?: string | null) => {
      if (coverObjectUrl) {
        URL.revokeObjectURL(coverObjectUrl);
        coverObjectUrl = null;
      }

      if (selectedCover) {
        coverObjectUrl = URL.createObjectURL(selectedCover);
        coverName.textContent = selectedCover.name;
        coverPreview.innerHTML = `<img class="w-full h-full object-cover" src="${coverObjectUrl}" alt="Location cover preview" data-preview-url="${coverObjectUrl}" data-preview-alt="Location cover preview"/>`;
        return;
      }

      if (selectedCoverRemoteUrl) {
        coverName.textContent = 'Selected from gallery';
        coverPreview.innerHTML = `<img class="w-full h-full object-cover" src="${selectedCoverRemoteUrl}" alt="Location cover from gallery" data-preview-url="${selectedCoverRemoteUrl}" data-preview-alt="Location cover from gallery"/>`;
        return;
      }

      if (coverUrl) {
        coverName.textContent = 'Current cover image';
        coverPreview.innerHTML = `<img class="w-full h-full object-cover" src="${coverUrl}" alt="Location cover" data-preview-url="${coverUrl}" data-preview-alt="Location cover"/>`;
        return;
      }

      coverName.textContent = '';
      coverPreview.innerHTML = 'No cover selected';
    };

    const openImagePreview = (url: string, alt?: string) => {
      if (!previewImage) return;
      const value = String(url || '').trim();
      if (!value) return;
      previewImage.src = value;
      previewImage.alt = alt || 'Image preview';
      imagePreviewModal.classList.remove('hidden');
      imagePreviewModal.classList.add('flex');
      document.body.classList.add('overflow-hidden');
    };

    const closeImagePreview = () => {
      if (!previewImage) return;
      imagePreviewModal.classList.add('hidden');
      imagePreviewModal.classList.remove('flex');
      previewImage.src = '';
      document.body.classList.remove('overflow-hidden');
    };

    const coverDropActive = (active: boolean) => {
      coverPreview.classList.toggle('ring-2', active);
      coverPreview.classList.toggle('ring-primary', active);
      coverPreview.classList.toggle('ring-offset-2', active);
    };

    const fillRegions = (countryId?: number) => {
      const options = regions.filter((item) => (countryId ? item.country === countryId : true));
      regionSelect.innerHTML = '<option value="">Select region</option>';
      for (const item of options) {
        const option = document.createElement('option');
        option.value = String(item.id);
        option.textContent = item.name;
        regionSelect.append(option);
      }
      if (presetRegionId) {
        regionSelect.value = String(presetRegionId);
      }
      const selectedRegion = regionSelect.value ? Number(regionSelect.value) : undefined;
      fillCounties(selectedRegion);
    };

    const fillCounties = (regionId?: number) => {
      const options = counties.filter((item) => (regionId ? item.region === regionId : true));
      countySelect.innerHTML = '<option value="">Select county</option>';
      for (const item of options) {
        const option = document.createElement('option');
        option.value = String(item.id);
        option.textContent = item.name;
        countySelect.append(option);
      }
      if (presetCountyId) {
        countySelect.value = String(presetCountyId);
      }
      const selectedCounty = countySelect.value ? Number(countySelect.value) : undefined;
      fillSettlements(selectedCounty);
    };

    const fillSettlements = (countyId?: number) => {
      const options = settlements.filter((item) => (countyId ? item.county === countyId : true));
      settlementSelect.innerHTML = '<option value="">Select settlement</option>';
      for (const item of options) {
        const option = document.createElement('option');
        option.value = String(item.id);
        option.textContent = item.name;
        settlementSelect.append(option);
      }
      if (presetSettlementId) {
        settlementSelect.value = String(presetSettlementId);
      }
    };

    const loadDictionaries = async () => {
      try {
        const [
          countriesPayload,
          regionsPayload,
          countiesPayload,
          settlementsPayload,
          categoriesPayload,
          tagsPayload,
          roadTypesPayload,
          difficultiesPayload,
          bestSeasonsPayload,
        ] =
          await Promise.all([
            listCountries(),
            listRegions(),
            listCounties(),
            listSettlements(),
            listCategories(),
            listTags(),
            listLocationRoadTypes(),
            listLocationDifficulties(),
            listLocationBestSeasons(),
          ]);
        if (disposed) return;
        countries = countriesPayload;
        regions = regionsPayload;
        counties = countiesPayload;
        settlements = settlementsPayload;
        categories = categoriesPayload;
        tags = tagsPayload;
        roadTypes = roadTypesPayload;
        difficulties = difficultiesPayload;
        bestSeasons = bestSeasonsPayload;

        countrySelect.innerHTML = '<option value="">Select country</option>';
        for (const item of countries) {
          const option = document.createElement('option');
          option.value = String(item.id);
          option.textContent = item.name;
          countrySelect.append(option);
        }
        if (presetCountryId) {
          countrySelect.value = String(presetCountryId);
        }
        fillRegions(presetCountryId ?? undefined);

        categorySelect.innerHTML = '<option value="">Select category</option>';
        for (const item of categories) {
          const option = document.createElement('option');
          option.value = String(item.id);
          option.textContent = item.name;
          categorySelect.append(option);
        }

        tagSelect.innerHTML = '<option value="">Select tag</option>';
        for (const item of tags) {
          const option = document.createElement('option');
          option.value = String(item.id);
          option.textContent = item.name;
          tagSelect.append(option);
        }

        roadTypeSelect.innerHTML = '<option value="">Select road type</option>';
        for (const item of roadTypes.filter((entry) => entry.is_active)) {
          const option = document.createElement('option');
          option.value = String(item.id);
          option.textContent = item.name;
          roadTypeSelect.append(option);
        }
        if (presetRoadTypeId) {
          roadTypeSelect.value = String(presetRoadTypeId);
        }

        difficultySelect.innerHTML = '<option value="">Select difficulty</option>';
        for (const item of difficulties.filter((entry) => entry.is_active)) {
          const option = document.createElement('option');
          option.value = String(item.id);
          option.textContent = item.name;
          difficultySelect.append(option);
        }
        if (presetDifficultyId) {
          difficultySelect.value = String(presetDifficultyId);
        }

        bestSeasonSelect.innerHTML = '<option value="">Select best season</option>';
        for (const item of bestSeasons.filter((entry) => entry.is_active)) {
          const option = document.createElement('option');
          option.value = String(item.id);
          option.textContent = item.name;
          bestSeasonSelect.append(option);
        }
        if (presetBestSeasonId) {
          bestSeasonSelect.value = String(presetBestSeasonId);
        }

        renderSelectedChips(categoriesSelected, selectedCategoryIds, categories, 'category');
        renderSelectedChips(tagsSelected, selectedTagIds, tags, 'tag');
      } catch {
        // keep page usable
      }
    };

    const pollTaskAndLoadProposal = async (taskId: number): Promise<AiProposal | null> => {
      for (let attempt = 0; attempt < 30; attempt += 1) {
        if (disposed) return null;
        const task = await getAiTask(taskId);
        if (task.status === 'completed') {
          const proposalsPayload = await listAiProposals({ task: taskId });
          const first =
            proposalsPayload.results.find((item) => item.proposal_type === 'location_create') ?? proposalsPayload.results[0];
          return first ?? null;
        }
        if (task.status === 'failed') {
          throw new Error(task.error_message || 'AI task failed.');
        }
        await new Promise((resolve) => {
          window.setTimeout(resolve, 1200);
        });
      }
      throw new Error('AI generation timeout. Please try again.');
    };

    const resolveDictionaryId = (raw: unknown, source: DictionaryItem[]): number | undefined => {
      if (typeof raw === 'number' && Number.isFinite(raw)) {
        const found = source.find((item) => item.id === raw);
        return found?.id;
      }
      if (typeof raw !== 'string') return undefined;
      const normalized = raw.trim().toLowerCase();
      if (!normalized) return undefined;
      const found = source.find((item) => item.slug.toLowerCase() === normalized || item.name.toLowerCase() === normalized);
      return found?.id;
    };

    const applyAiPayload = (payload: Record<string, unknown>) => {
      const title = typeof payload.title === 'string' ? payload.title.trim() : '';
      if (title) {
        titleInput.value = title;
      }

      const slugValue = typeof payload.slug === 'string' ? payload.slug.trim() : '';
      if (slugValue) {
        slugInput.value = slugValue;
        slugManuallyEdited = true;
      } else if (titleInput.value.trim()) {
        slugInput.value = slugify(titleInput.value.trim());
      }

      const description = typeof payload.description === 'string' ? payload.description : '';
      if (description) {
        descriptionInput.value = description;
      }

      const contentBlocks = Array.isArray(payload.content_blocks) ? payload.content_blocks : [];
      if (contentBlocks.length) {
        contentBlocksEditor.setBlocks(contentBlocks as any);
        contentBlocksInput.value = stringifyContentBlocks(contentBlocks as any);
      }

      const latValue = payload.latitude;
      const lonValue = payload.longitude;
      if (latValue !== undefined) {
        latInput.value = String(latValue).replace(',', '.');
      }
      if (lonValue !== undefined) {
        lonInput.value = String(lonValue).replace(',', '.');
      }
      onCoordinatesInput();

      const countryId = resolveDictionaryId(payload.country, countries);
      if (countryId) {
        countrySelect.value = String(countryId);
        fillRegions(countryId);
      }
      const regionId = resolveDictionaryId(payload.region, regions);
      if (regionId) {
        regionSelect.value = String(regionId);
        fillCounties(regionId);
      }
      const countyId = resolveDictionaryId(payload.county, counties);
      if (countyId) {
        countySelect.value = String(countyId);
        fillSettlements(countyId);
      }
      const settlementId = resolveDictionaryId(payload.settlement, settlements);
      if (settlementId) {
        settlementSelect.value = String(settlementId);
      }

      const roadTypeId = resolveDictionaryId(payload.road_type, roadTypes);
      if (roadTypeId) {
        roadTypeSelect.value = String(roadTypeId);
      }
      const difficultyId = resolveDictionaryId(payload.difficulty, difficulties);
      if (difficultyId) {
        difficultySelect.value = String(difficultyId);
      }
      const bestSeasonId = resolveDictionaryId(payload.best_season, bestSeasons);
      if (bestSeasonId) {
        bestSeasonSelect.value = String(bestSeasonId);
      }

      if (typeof payload.safety_notes === 'string') {
        safetyNotesInput.value = payload.safety_notes;
      }
      if (typeof payload.access_rules === 'string') {
        accessRulesInput.value = payload.access_rules;
      }
      if (typeof payload.is_featured === 'boolean') {
        featured = payload.is_featured;
        applyFeaturedUi();
      }

      const incomingFacts = Array.isArray(payload.facts) ? payload.facts : [];
      if (incomingFacts.length) {
        facts = incomingFacts
          .map((item) => {
            if (item && typeof item === 'object') {
              const text = String((item as { text?: unknown }).text ?? '').trim();
              if (!text) return null;
              return { id: factCounter++, text };
            }
            const text = String(item ?? '').trim();
            if (!text) return null;
            return { id: factCounter++, text };
          })
          .filter(Boolean) as FactItem[];
        if (facts.length === 0) facts = [{ id: factCounter++, text: '' }];
        renderFacts();
      }

      const incomingCategoryIds = Array.isArray(payload.category_ids) ? payload.category_ids : [];
      const resolvedCategories = incomingCategoryIds
        .map((item) => resolveDictionaryId(item, categories))
        .filter((item): item is number => Boolean(item));
      if (resolvedCategories.length) {
        selectedCategoryIds = Array.from(new Set(resolvedCategories));
        renderSelectedChips(categoriesSelected, selectedCategoryIds, categories, 'category');
      }

      const incomingTagIds = Array.isArray(payload.tag_ids) ? payload.tag_ids : [];
      const resolvedTags = incomingTagIds
        .map((item) => resolveDictionaryId(item, tags))
        .filter((item): item is number => Boolean(item));
      if (resolvedTags.length) {
        selectedTagIds = Array.from(new Set(resolvedTags));
        renderSelectedChips(tagsSelected, selectedTagIds, tags, 'tag');
      }
    };

    const buildPayload = (): CreateLocationPayload | null => {
      const title = titleInput.value.trim();
      const description = descriptionInput.value.trim();
      const latitude = latInput.value.trim();
      const longitude = lonInput.value.trim();
      if (!title) {
        showMessage('Title is required.', 'error');
        return null;
      }
      if (!latitude || !longitude) {
        showMessage('Latitude and longitude are required.', 'error');
        return null;
      }
      const latNormalized = normalizeCoordinate(latitude, -90, 90);
      const lonNormalized = normalizeCoordinate(longitude, -180, 180);
      if (!latNormalized || !lonNormalized) {
        showMessage(
          'Coordinates must be valid numbers: latitude [-90..90], longitude [-180..180], up to 6 decimals.',
          'error'
        );
        return null;
      }
      latInput.value = latNormalized.value;
      lonInput.value = lonNormalized.value;
      let parsedBlocks: ReturnType<typeof contentBlocksEditor.getBlocks> = null;
      try {
        parsedBlocks = contentBlocksEditor.getBlocks();
      } catch (error) {
        showMessage(error instanceof Error ? error.message : 'Invalid structured content.', 'error');
        return null;
      }

      return {
        title,
        slug: slugInput.value.trim() || slugify(title),
        description,
        content_format: parsedBlocks?.length ? ('blocks_v1' as const) : undefined,
        content_blocks: parsedBlocks || undefined,
        latitude: latNormalized.value,
        longitude: lonNormalized.value,
        country: countrySelect.value ? Number(countrySelect.value) : undefined,
        region: regionSelect.value ? Number(regionSelect.value) : undefined,
        road_type: roadTypeSelect.value ? Number(roadTypeSelect.value) : undefined,
        difficulty: difficultySelect.value ? Number(difficultySelect.value) : undefined,
        best_season: bestSeasonSelect.value ? Number(bestSeasonSelect.value) : undefined,
        safety_notes: safetyNotesInput.value.trim(),
        access_rules: accessRulesInput.value.trim(),
        is_featured: featured,
        county: countySelect.value ? Number(countySelect.value) : undefined,
        settlement: settlementSelect.value ? Number(settlementSelect.value) : undefined,
        facts: facts
          .filter((fact) => fact.text.trim())
          .map((fact, index) => ({ text: fact.text.trim(), sort_order: index + 1 })),
        category_ids: selectedCategoryIds,
        tag_ids: selectedTagIds,
      };
    };

    const uploadPendingMedia = async (locationId: number) => {
      for (const media of pendingMedia) {
        const formData = new FormData();
        formData.append('kind', 'image');
        formData.append('file', media.file);
        await uploadLocationMedia(locationId, formData);
      }
      for (const media of pendingMedia) {
        URL.revokeObjectURL(media.url);
      }
      pendingMedia = [];
    };

    const submit = async (mode: 'publish' | 'draft' | 'suggest') => {
      const payload = buildPayload();
      if (!payload) return;
      setButtonLoading(true, mode);
      try {
        let savedId = editingId;
        if (mode === 'suggest' && !editingId) {
          const created = await suggestLocation(payload);
          savedId = created.id;
        } else if (editingId) {
          if (editingStatus === 'rejected') {
            await resubmitLocation(editingId, payload);
          } else {
            await patchLocation(editingId, payload);
          }
        } else {
          const created = await createLocation(payload);
          savedId = created.id;
        }

        if (!savedId) {
          throw new Error('Location id not found after save.');
        }
        editingId = savedId;

        if (selectedCover) {
          await setLocationCover(savedId, { coverImage: selectedCover });
          selectedCover = null;
          selectedCoverMediaId = null;
          selectedCoverRemoteUrl = null;
        } else if (selectedCoverMediaId) {
          await setLocationCover(savedId, { mediaId: selectedCoverMediaId });
          selectedCoverMediaId = null;
          selectedCoverRemoteUrl = null;
        }

        await uploadPendingMedia(savedId);
        if (mode === 'publish') {
          await publishLocation(savedId);
        }

        showMessage('Location saved successfully.', 'success');
        modifiedAt = new Date();
        renderLastModified();
        window.setTimeout(() => navigateBackToList(), 350);
      } catch (error) {
        showMessage(extractValidationMessage(error, 'Failed to save location.'), 'error');
      } finally {
        setButtonLoading(false, mode);
      }
    };

    const loadExistingForEdit = async () => {
      const rawId = searchParams.get('id');
      if (!rawId) {
        setStatusBadge('draft');
        return;
      }
      const id = Number(rawId);
      if (Number.isNaN(id)) {
        showMessage('Invalid location id for edit.', 'error');
        return;
      }
      try {
        const item = await getLocation(id);
        if (disposed) return;
        editingId = item.id;
        editingStatus = item.status;
        titleInput.value = item.title;
        slugInput.value = item.slug;
        if (isFallbackSlug(item.slug) && item.title.trim()) {
          slugInput.value = slugify(item.title.trim());
        }
        descriptionInput.value = item.description;
        contentBlocksEditor.setBlocks(item.content_blocks);
        latInput.value = item.latitude;
        lonInput.value = item.longitude;
        presetRoadTypeId = item.road_type;
        presetDifficultyId = item.difficulty;
        presetBestSeasonId = item.best_season;
        roadTypeSelect.value = item.road_type ? String(item.road_type) : '';
        difficultySelect.value = item.difficulty ? String(item.difficulty) : '';
        bestSeasonSelect.value = item.best_season ? String(item.best_season) : '';
        safetyNotesInput.value = item.safety_notes || '';
        accessRulesInput.value = item.access_rules || '';
        featured = item.is_featured;
        modifiedAt = item.updated_at ? new Date(item.updated_at) : new Date();
        if (Number.isNaN(modifiedAt.getTime())) {
          modifiedAt = new Date();
        }
        renderLastModified();
        presetCountryId = item.country;
        presetRegionId = item.region;
        presetCountyId = item.county;
        presetSettlementId = item.settlement;
        selectedCategoryIds = [...item.category_ids];
        selectedTagIds = [...item.tag_ids];
        facts = (item.facts ?? []).map((fact) => ({ id: fact.id ?? factCounter++, text: fact.text }));
        if (facts.length === 0) {
          facts = [{ id: factCounter++, text: '' }];
        }
        applyFeaturedUi();
        setStatusBadge(item.status);
        suggestBtn.classList.add('hidden');
        if (countrySelect.options.length > 1 && item.country) {
          countrySelect.value = String(item.country);
          fillRegions(item.country);
        }
        if (regionSelect.options.length > 1 && item.region) {
          regionSelect.value = String(item.region);
          fillCounties(item.region);
        }
        if (countySelect.options.length > 1 && item.county) {
          countySelect.value = String(item.county);
          fillSettlements(item.county);
        }
        if (settlementSelect.options.length > 1 && item.settlement) {
          settlementSelect.value = String(item.settlement);
        }
        renderSelectedChips(categoriesSelected, selectedCategoryIds, categories, 'category');
        renderSelectedChips(tagsSelected, selectedTagIds, tags, 'tag');
        existingMedia = (await listAllLocationMedia(item.id)).results.filter((entry) => !isContentBlockMediaTitle(entry.title));
        renderCoverPreview(item.cover_image ?? null);
        renderMediaPreview();
        renderFacts();
        onCoordinatesInput();
      } catch (error) {
        showMessage(extractValidationMessage(error, 'Failed to load location for edit.'), 'error');
      }
    };

    const onTitleInput = () => {
      if (!slugManuallyEdited) {
        slugInput.value = slugify(titleInput.value);
      }
    };
    const onSlugInput = () => {
      slugManuallyEdited = Boolean(slugInput.value.trim());
    };
    const onCountryChange = () => {
      const value = countrySelect.value ? Number(countrySelect.value) : undefined;
      fillRegions(value);
    };
    const onRegionChange = () => {
      const value = regionSelect.value ? Number(regionSelect.value) : undefined;
      fillCounties(value);
    };
    const onCountyChange = () => {
      const value = countySelect.value ? Number(countySelect.value) : undefined;
      fillSettlements(value);
    };
    const onCoordinatesInput = () => {
      const lat = normalizeCoordinate(latInput.value, -90, 90);
      const lon = normalizeCoordinate(lonInput.value, -180, 180);
      if (!lat || !lon || !markerInstance || !mapInstance) return;
      markerInstance.setLatLng([lat.numberValue, lon.numberValue]);
      mapInstance.setView([lat.numberValue, lon.numberValue], mapInstance.getZoom(), { animate: false });
    };
    const onToggleFeatured = () => {
      featured = !featured;
      applyFeaturedUi();
    };
    const onCancel = () => navigateBackToList();
    const onGenerateWithAi = async () => {
      const title = titleInput.value.trim();
      if (!title) {
        showMessage('Введите название точки в поле Title, затем запустите AI.', 'error');
        return;
      }
      setAiLoading(true);
      try {
        const currentLat = normalizeCoordinate(latInput.value, -90, 90);
        const currentLon = normalizeCoordinate(lonInput.value, -180, 180);
        const requirementsParts = [
          `Сгенерируй максимально полный payload для локации: ${title}.`,
          descriptionInput.value.trim() ? `Текущий черновик описания: ${descriptionInput.value.trim()}` : '',
          'Все текстовые поля должны быть строго на русском языке.',
          'Сформируй реалистичное описание с историческим/фактологическим контекстом, без выдуманных утверждений.',
          'Обязательно заполни content_format=blocks_v1 и content_blocks (heading + paragraph + list + callout).',
          'Заполни максимально возможное число полей и используй id справочников, если они есть.',
        ].filter(Boolean);
        const task = await generateLocationDescriptionTask({
          title,
          requirements: requirementsParts.join('\n'),
          latitude: currentLat?.numberValue,
          longitude: currentLon?.numberValue,
        });
        const proposal = await pollTaskAndLoadProposal(task.id);
        if (!proposal || typeof proposal.payload !== 'object' || !proposal.payload) {
          throw new Error('AI returned empty payload.');
        }
        applyAiPayload(proposal.payload as Record<string, unknown>);
        showMessage('AI заполнил поля. Проверь и сохрани локацию.', 'success');
      } catch (error) {
        showMessage(extractValidationMessage(error, 'AI не смог заполнить данные.'), 'error');
      } finally {
        setAiLoading(false);
      }
    };
    const onAiGenerateClick = () => {
      void onGenerateWithAi();
    };
    const onImportJson = async () => {
      const raw = await openJsonImportDialog();
      if (raw === null) return;
      if (!raw.trim()) {
        showMessage('JSON payload is empty.', 'error');
        return;
      }

      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(raw) as Record<string, unknown>;
      } catch {
        showMessage('Invalid JSON format.', 'error');
        return;
      }

      setImportLoading(true);
      try {
        if (editingId) {
          const updated = await importLocationIntoExistingFromJson(editingId, parsed);
          showMessage(`Location updated from JSON: #${updated.id}.`, 'success');
          await loadExistingForEdit();
        } else {
          const created = await importLocationFromJson(parsed);
          showMessage(`Location imported: #${created.id}. Opening editor...`, 'success');
          window.setTimeout(() => navigate(`/locations/create?id=${created.id}`), 250);
        }
      } catch (error) {
        showMessage(extractValidationMessage(error, 'Failed to import JSON location.'), 'error');
      } finally {
        setImportLoading(false);
      }
    };
    const onImportJsonClick = () => {
      void onImportJson();
    };
    const onDraft = () => void submit('draft');
    const onPublish = () => void submit('publish');
    const onSuggest = () => void submit('suggest');
    const onMediaPick = () => mediaInput.click();
    const onCoverPick = () => coverInput.click();

    const onMediaChange = () => {
      const files = Array.from(mediaInput.files ?? []);
      if (files.length === 0) return;
      for (const file of files) {
        pendingMedia.push({ file, url: URL.createObjectURL(file) });
      }
      mediaInput.value = '';
      renderMediaPreview();
    };

    const onCoverChange = () => {
      selectedCover = coverInput.files?.[0] ?? null;
      selectedCoverMediaId = null;
      selectedCoverRemoteUrl = null;
      coverInput.value = '';
      renderCoverPreview();
    };

    const onMediaPreviewClick = async (event: Event) => {
      const target = event.target as HTMLElement;
      const moveBtn = target.closest<HTMLButtonElement>('button[data-existing-media-move][data-existing-media-index]');
      if (moveBtn && editingId) {
        const direction = moveBtn.dataset.existingMediaMove;
        const index = Number(moveBtn.dataset.existingMediaIndex ?? '');
        if (Number.isNaN(index)) return;
        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        if (swapIndex < 0 || swapIndex >= existingMedia.length) return;
        const next = [...existingMedia];
        const tmp = next[index];
        next[index] = next[swapIndex];
        next[swapIndex] = tmp;
        existingMedia = next;
        renderMediaPreview();
        try {
          existingMedia = await reorderLocationMedia(
            editingId,
            existingMedia.map((item, idx) => ({ id: item.id, sort_order: idx + 1 }))
          );
          renderMediaPreview();
        } catch (error) {
          showMessage(extractValidationMessage(error, 'Failed to reorder media.'), 'error');
        }
        return;
      }

      const existingBtn = target.closest<HTMLButtonElement>('button[data-existing-media-id]');
      if (existingBtn && editingId) {
        const mediaId = Number(existingBtn.dataset.existingMediaId ?? '');
        if (Number.isNaN(mediaId)) return;
        try {
          await deleteLocationMedia(editingId, mediaId);
          existingMedia = existingMedia.filter((item) => item.id !== mediaId);
          renderMediaPreview();
        } catch (error) {
          showMessage(extractValidationMessage(error, 'Failed to delete media.'), 'error');
        }
        return;
      }

      const pendingBtn = target.closest<HTMLButtonElement>('button[data-pending-media-index]');
      if (pendingBtn) {
        const idx = Number(pendingBtn.dataset.pendingMediaIndex ?? '');
        if (Number.isNaN(idx)) return;
        const removed = pendingMedia[idx];
        if (removed) {
          URL.revokeObjectURL(removed.url);
        }
        pendingMedia = pendingMedia.filter((_, index) => index !== idx);
        renderMediaPreview();
        return;
      }

      const previewTarget = target.closest<HTMLElement>('[data-preview-url]');
      if (previewTarget?.dataset.previewUrl) {
        openImagePreview(previewTarget.dataset.previewUrl, previewTarget.dataset.previewAlt);
      }
    };

    const onCoverPreviewClick = (event: Event) => {
      const target = event.target as HTMLElement;
      const previewTarget = target.closest<HTMLElement>('[data-preview-url]');
      if (!previewTarget?.dataset.previewUrl) return;
      openImagePreview(previewTarget.dataset.previewUrl, previewTarget.dataset.previewAlt);
    };

    const onMediaPreviewDragStart = (event: DragEvent) => {
      const target = event.target as HTMLElement;
      const tile = target.closest<HTMLElement>('[data-drag-media][data-drag-index]');
      if (!tile || !event.dataTransfer) return;
      const kind = tile.dataset.dragMedia;
      const index = Number(tile.dataset.dragIndex ?? '');
      if (!kind || Number.isNaN(index)) return;
      const payload: DragMediaPayload = { kind: kind === 'existing' ? 'existing' : 'pending', index };
      event.dataTransfer.setData('application/json', JSON.stringify(payload));
      event.dataTransfer.effectAllowed = 'move';
    };

    const onCoverDragOver = (event: DragEvent) => {
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
      coverDropActive(true);
    };

    const onCoverDragLeave = () => {
      coverDropActive(false);
    };

    const onCoverDrop = async (event: DragEvent) => {
      event.preventDefault();
      coverDropActive(false);
      const raw = event.dataTransfer?.getData('application/json');
      if (!raw) return;
      let payload: DragMediaPayload | null = null;
      try {
        payload = JSON.parse(raw) as DragMediaPayload;
      } catch {
        payload = null;
      }
      if (!payload) return;

      if (payload.kind === 'pending') {
        const media = pendingMedia[payload.index];
        if (!media) return;
        selectedCover = media.file;
        selectedCoverMediaId = null;
        selectedCoverRemoteUrl = null;
        pendingMedia = pendingMedia.filter((_, idx) => idx !== payload.index);
        renderCoverPreview();
        renderMediaPreview();
        showMessage('Image moved to cover.', 'success');
        return;
      }

      const media = existingMedia[payload.index];
      if (!media) return;
      const sourceUrl = media.file_large_url || media.file_url || media.file_thumbnail_url;
      if (!sourceUrl) return;
      selectedCover = null;
      selectedCoverMediaId = media.id;
      selectedCoverRemoteUrl = sourceUrl;
      renderCoverPreview();
      showMessage('Image selected as cover from gallery.', 'success');
    };

    const onCoverDropEvent = (event: Event) => {
      void onCoverDrop(event as DragEvent);
    };

    const onPreviewModalClick = (event: Event) => {
      const target = event.target as HTMLElement;
      if (target === imagePreviewModal || target.closest('[data-preview-close="true"]')) {
        closeImagePreview();
      }
    };

    const onPreviewKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeImagePreview();
      }
    };

    const onAddFact = () => {
      facts.push({ id: factCounter++, text: '' });
      renderFacts();
    };

    const onFactsInput = (event: Event) => {
      const target = event.target as HTMLElement;
      const input = target.closest<HTMLInputElement>('input[data-fact-id]');
      if (!input) return;
      const id = Number(input.dataset.factId ?? '');
      if (Number.isNaN(id)) return;
      facts = facts.map((fact) => (fact.id === id ? { ...fact, text: input.value } : fact));
    };

    const onFactsClick = (event: Event) => {
      const target = event.target as HTMLElement;
      const removeBtn = target.closest<HTMLButtonElement>('button[data-remove-fact-id]');
      if (!removeBtn) return;
      const id = Number(removeBtn.dataset.removeFactId ?? '');
      if (Number.isNaN(id)) return;
      facts = facts.filter((fact) => fact.id !== id);
      if (facts.length === 0) facts.push({ id: factCounter++, text: '' });
      renderFacts();
    };

    const onCategoryAdd = () => {
      const id = Number(categorySelect.value);
      if (Number.isNaN(id)) return;
      if (!selectedCategoryIds.includes(id)) {
        selectedCategoryIds.push(id);
      }
      renderSelectedChips(categoriesSelected, selectedCategoryIds, categories, 'category');
    };

    const onTagAdd = () => {
      const id = Number(tagSelect.value);
      if (Number.isNaN(id)) return;
      if (!selectedTagIds.includes(id)) {
        selectedTagIds.push(id);
      }
      renderSelectedChips(tagsSelected, selectedTagIds, tags, 'tag');
    };

    const onCategorySelectedClick = (event: Event) => {
      const target = event.target as HTMLElement;
      const removeBtn = target.closest<HTMLButtonElement>('button[data-remove-category-id]');
      if (!removeBtn) return;
      const id = Number(removeBtn.dataset.removeCategoryId ?? '');
      if (Number.isNaN(id)) return;
      selectedCategoryIds = selectedCategoryIds.filter((item) => item !== id);
      renderSelectedChips(categoriesSelected, selectedCategoryIds, categories, 'category');
    };

    const onTagSelectedClick = (event: Event) => {
      const target = event.target as HTMLElement;
      const removeBtn = target.closest<HTMLButtonElement>('button[data-remove-tag-id]');
      if (!removeBtn) return;
      const id = Number(removeBtn.dataset.removeTagId ?? '');
      if (Number.isNaN(id)) return;
      selectedTagIds = selectedTagIds.filter((item) => item !== id);
      renderSelectedChips(tagsSelected, selectedTagIds, tags, 'tag');
    };

    const ensureLeaflet = async (): Promise<void> => {
      if ((window as unknown as { L?: unknown }).L) return;
      const cssHref = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      const scriptSrc = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';

      if (!document.querySelector(`link[href="${cssHref}"]`)) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = cssHref;
        document.head.append(link);
      }

      if (!document.querySelector(`script[src="${scriptSrc}"]`)) {
        await new Promise<void>((resolve, reject) => {
          const script = document.createElement('script');
          script.src = scriptSrc;
          script.async = true;
          script.onload = () => resolve();
          script.onerror = () => reject(new Error('Leaflet load failed'));
          document.body.append(script);
        });
      } else if (!(window as unknown as { L?: unknown }).L) {
        await new Promise<void>((resolve) => {
          window.setTimeout(() => resolve(), 150);
        });
      }
    };

    const initMap = async () => {
      try {
        await ensureLeaflet();
      } catch {
        return;
      }
      if (disposed) return;
      const L = (window as unknown as { L?: any }).L;
      if (!L) return;

      mapPlaceholder.classList.add('hidden');

      const initialLat = normalizeCoordinate(latInput.value || '53.900000', -90, 90);
      const initialLon = normalizeCoordinate(lonInput.value || '27.566700', -180, 180);
      const lat = initialLat?.numberValue ?? 53.9;
      const lon = initialLon?.numberValue ?? 27.5667;

      const map = L.map(mapContainer, { zoomControl: true }).setView([lat, lon], 10);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
      }).addTo(map);
      const marker = L.marker([lat, lon], { draggable: true }).addTo(map);

      // Keep Leaflet layers under sticky layout elements.
      mapContainer.style.zIndex = '0';
      mapContainer.style.isolation = 'isolate';
      mapContainer.querySelectorAll<HTMLElement>('.leaflet-pane, .leaflet-top, .leaflet-bottom').forEach((el) => {
        el.style.zIndex = '1';
      });

      map.on('click', (e: { latlng: { lat: number; lng: number } }) => {
        const nextLat = e.latlng.lat.toFixed(6);
        const nextLon = e.latlng.lng.toFixed(6);
        latInput.value = nextLat;
        lonInput.value = nextLon;
        marker.setLatLng([Number(nextLat), Number(nextLon)]);
      });
      marker.on('dragend', (e: { target: { getLatLng: () => { lat: number; lng: number } } }) => {
        const point = e.target.getLatLng();
        latInput.value = point.lat.toFixed(6);
        lonInput.value = point.lng.toFixed(6);
      });

      mapInstance = map;
      markerInstance = marker;
      window.setTimeout(() => map.invalidateSize(), 120);
    };

    const onMediaPreviewClickHandler = (event: Event) => {
      void onMediaPreviewClick(event);
    };

    titleInput.addEventListener('input', onTitleInput);
    slugInput.addEventListener('input', onSlugInput);
    countrySelect.addEventListener('change', onCountryChange);
    regionSelect.addEventListener('change', onRegionChange);
    countySelect.addEventListener('change', onCountyChange);
    latInput.addEventListener('input', onCoordinatesInput);
    lonInput.addEventListener('input', onCoordinatesInput);
    featuredToggle.addEventListener('click', onToggleFeatured);
    cancelBtn.addEventListener('click', onCancel);
    importBtn.addEventListener('click', onImportJsonClick);
    aiBtn.addEventListener('click', onAiGenerateClick);
    suggestBtn.addEventListener('click', onSuggest);
    mediaBtn.addEventListener('click', onMediaPick);
    mediaInput.addEventListener('change', onMediaChange);
    mediaPreview.addEventListener('click', onMediaPreviewClickHandler);
    mediaPreview.addEventListener('dragstart', onMediaPreviewDragStart);
    coverBtn.addEventListener('click', onCoverPick);
    coverInput.addEventListener('change', onCoverChange);
    coverPreview.addEventListener('click', onCoverPreviewClick);
    coverPreview.addEventListener('dragover', onCoverDragOver);
    coverPreview.addEventListener('dragleave', onCoverDragLeave);
    coverPreview.addEventListener('drop', onCoverDropEvent);
    imagePreviewModal.addEventListener('click', onPreviewModalClick);
    document.addEventListener('keydown', onPreviewKeydown);
    draftBtn.addEventListener('click', onDraft);
    publishBtn.addEventListener('click', onPublish);
    addFactBtn.addEventListener('click', onAddFact);
    factsList.addEventListener('input', onFactsInput);
    factsList.addEventListener('click', onFactsClick);
    categoryAddBtn.addEventListener('click', onCategoryAdd);
    tagAddBtn.addEventListener('click', onTagAdd);
    categoriesSelected.addEventListener('click', onCategorySelectedClick);
    tagsSelected.addEventListener('click', onTagSelectedClick);

    applyFeaturedUi();
    updateSlugPrefix();
    setStatusBadge('draft');
    modifiedAt = new Date();
    renderLastModified();
    facts = [{ id: factCounter++, text: '' }];
    renderFacts();
    renderCoverPreview();
    renderMediaPreview();
    void loadDictionaries();
    void loadExistingForEdit();
    void initMap();
    modifiedTimer = window.setInterval(renderLastModified, 30_000);

    return () => {
      disposed = true;
      if (modifiedTimer) {
        window.clearInterval(modifiedTimer);
      }
      for (const media of pendingMedia) {
        URL.revokeObjectURL(media.url);
      }
      if (coverObjectUrl) {
        URL.revokeObjectURL(coverObjectUrl);
      }
      titleInput.removeEventListener('input', onTitleInput);
      slugInput.removeEventListener('input', onSlugInput);
      countrySelect.removeEventListener('change', onCountryChange);
      regionSelect.removeEventListener('change', onRegionChange);
      countySelect.removeEventListener('change', onCountyChange);
      latInput.removeEventListener('input', onCoordinatesInput);
      lonInput.removeEventListener('input', onCoordinatesInput);
      featuredToggle.removeEventListener('click', onToggleFeatured);
      cancelBtn.removeEventListener('click', onCancel);
      importBtn.removeEventListener('click', onImportJsonClick);
      aiBtn.removeEventListener('click', onAiGenerateClick);
      suggestBtn.removeEventListener('click', onSuggest);
      mediaBtn.removeEventListener('click', onMediaPick);
      mediaInput.removeEventListener('change', onMediaChange);
      mediaPreview.removeEventListener('click', onMediaPreviewClickHandler);
      mediaPreview.removeEventListener('dragstart', onMediaPreviewDragStart);
      coverBtn.removeEventListener('click', onCoverPick);
      coverInput.removeEventListener('change', onCoverChange);
      coverPreview.removeEventListener('click', onCoverPreviewClick);
      coverPreview.removeEventListener('dragover', onCoverDragOver);
      coverPreview.removeEventListener('dragleave', onCoverDragLeave);
      coverPreview.removeEventListener('drop', onCoverDropEvent);
      draftBtn.removeEventListener('click', onDraft);
      publishBtn.removeEventListener('click', onPublish);
      addFactBtn.removeEventListener('click', onAddFact);
      factsList.removeEventListener('input', onFactsInput);
      factsList.removeEventListener('click', onFactsClick);
      categoryAddBtn.removeEventListener('click', onCategoryAdd);
      tagAddBtn.removeEventListener('click', onTagAdd);
      categoriesSelected.removeEventListener('click', onCategorySelectedClick);
      tagsSelected.removeEventListener('click', onTagSelectedClick);
      contentBlocksEditor.destroy();
      if (mapInstance) {
        mapInstance.remove();
      }
      imagePreviewModal.removeEventListener('click', onPreviewModalClick);
      document.removeEventListener('keydown', onPreviewKeydown);
      imagePreviewModal.remove();
      document.body.classList.remove('overflow-hidden');
      flash.remove();
    };
  }, [navigate, searchParams]);

  return (
    <>
      <Helmet>
        <title>MotoJourney | Create Location</title>
      </Helmet>
      <div className="font-display bg-background-light text-slate-900 min-h-screen">
        <div ref={containerRef} dangerouslySetInnerHTML={{ __html: template }} />
      </div>
    </>
  );
}
